/**
 * Platform detection for the iOS (Capacitor) build.
 *
 * There are deliberately TWO answers to "are we on iOS?", and they are not
 * interchangeable:
 *
 *   IS_IOS_APP — BUILD-TIME. Use it to GATE code out of the bundle (routes, nav
 *     entries, subscription/Stripe UI). App.tsx statically imports every page with
 *     zero code splitting, so a build-time constant is the only thing webpack can
 *     branch on at compile time to actually DROP TeamManagementPage (~13k LOC) and
 *     the Stripe path from the iOS bundle. It is also the stronger guarantee for
 *     App Review: if the paywall isn't in the bundle, no runtime trick reaches it.
 *
 *   isNative() — RUNTIME. Use it to pick BEHAVIOR (native vs web auth, haptics,
 *     share sheet, status bar). It has to be runtime because the same iOS bundle is
 *     also what you get under `npm start` in a desktop browser, where the native
 *     plugins aren't there — behavior must degrade, not crash.
 *
 * Rule of thumb: gating what EXISTS -> IS_IOS_APP. Choosing HOW something happens
 * -> isNative().
 */
import { Capacitor } from '@capacitor/core';

/** True when built with REACT_APP_PLATFORM=ios (see the `build:ios` npm script). */
export const IS_IOS_APP = process.env.REACT_APP_PLATFORM === 'ios';

/** True only when actually running inside the Capacitor WebView. */
export const isNative = (): boolean => Capacitor.isNativePlatform();

/** The concrete Capacitor platform: 'ios' | 'android' | 'web'. */
export const getPlatform = (): string => Capacitor.getPlatform();
